// components/UserProfileChip.jsx
import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Chip, Popover, Box, Typography, MenuItem, Divider, ListItemIcon, ListItemText, Badge } from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import { accessToken, clearCredentials, userInfo } from "../../app/store/authSlice";
import { useLogoutMutation } from "../../app/store/api/authApi";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getRouteForUser } from "../utils/roleRoutes";
import PersonIcon from "@mui/icons-material/Person";
import { useGetCountOfReservationsByWorkshopIdQuery } from "../../app/store/api/reservationApi";

function truncateEmail(email, maxLength = 10) {
  if (!email) return "";
  return email.length > maxLength ? `${email.slice(0, maxLength)}…` : email;
}

export default function UserProfileChip() {
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const user = useSelector(userInfo);
  const dispatch = useDispatch();
  const [logout] = useLogoutMutation();
  const navigate = useNavigate();
  const { data: reservationsCount } = useGetCountOfReservationsByWorkshopIdQuery({ workshopId: user?.companyId }, { skip: !user?.companyId });

  const open = Boolean(anchorEl);
  const email = user?.email || "";

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    try {
      await toast.promise(logout().unwrap(), {
        loading: t("login.logoutLoading"),
        success: t("login.logoutSuccess"),
        error: t("login.logoutError"),
      });
    } catch (err) {
      console.error(t("login.logoutError"), err);
    } finally {
      dispatch(clearCredentials());
      handleClose();
    }
  };

  const handleNavigate = () => {
    handleClose();
    navigate(getRouteForUser(user), { replace: true });
  };

  if (!user) return null;

  return (
    <>
      <Badge badgeContent={reservationsCount?.new || 0} sx={{ "& .MuiBadge-badge": { bgcolor:"#00C9DB", color: "#fff", fontWeight: 500 } }} color='primary'>
        <Chip
          icon={<AccountCircleIcon />}
          label={truncateEmail(email)}
          onClick={handleClick}
          clickable
          variant='outlined'
          sx={{
            cursor: "pointer",
            fontWeight: 500,
            maxWidth: 200,
          }}
        />
      </Badge>

      <Popover open={open} anchorEl={anchorEl} onClose={handleClose} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
        <Box sx={{ minWidth: 220 }}>
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant='caption' color='text.secondary'>
              {t("login.signedInAs")}
            </Typography>
            <Typography variant='body2' sx={{ wordBreak: "break-all", fontWeight: 500 }}>
              {email}
            </Typography>
          </Box>

          <Divider />

          <MenuItem onClick={handleNavigate} sx={{ py: 1.2 }}>
            {user.groups.includes("admin") ? (
              <>
                <ListItemIcon>
                  <PersonIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText primary={t("authorized.adminDashboard")} />
              </>
            ) : (
              <>
                <ListItemIcon>
                  <PersonIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText primary={t("authorized.userDashboard")} />
                <ListItemText primary={reservationsCount?.new > 0 ? <Badge badgeContent={reservationsCount.new} sx={{ "& .MuiBadge-badge": { bgcolor:"#00C9DB", color: "#fff", fontWeight: 500 } }} color='primary' /> : ""} sx={{ textAlign: "right" }} />
              </>
            )}
          </MenuItem>
          <MenuItem onClick={handleLogout} sx={{ py: 1.2 }}>
            <ListItemIcon>
              <LogoutIcon fontSize='small' />
            </ListItemIcon>
            <ListItemText primary={t("login.logout")} />
          </MenuItem>
        </Box>
      </Popover>
    </>
  );
}
